import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { useAssignStudent, useInstitutionStudents } from '../hooks/useInstitution'

interface ClassAssignFormProps {
  readonly classId: string
  readonly className: string
}

/** Rattachement d'un élève déjà inscrit à l'établissement (spécifications § 9.3). */
export function ClassAssignForm({ classId, className }: ClassAssignFormProps) {
  const { t } = useTranslation()
  const [userId, setUserId] = useState('')
  const { data: students } = useInstitutionStudents()
  const assign = useAssignStudent()

  const selectable = (students ?? []).filter((student) => student.status === 'active')

  if (selectable.length === 0) {
    return null
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (userId === '') return

    assign.mutate({ classId, userId }, { onSuccess: () => setUserId('') })
  }

  const selectId = `assign-${classId}`

  return (
    <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-2">
      <span className="flex flex-1 flex-col gap-1">
        <label htmlFor={selectId} className="text-xs font-medium text-on-surface-variant">
          {t('institution.classes.assign_label', { class: className })}
        </label>
        <select
          id={selectId}
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
          className="w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">{t('institution.classes.assign_placeholder')}</option>
          {selectable.map((student) => (
            <option key={student.user_id} value={student.user_id}>
              {[student.first_name, student.last_name].filter(Boolean).join(' ')}
            </option>
          ))}
        </select>
      </span>

      <Button type="submit" size="sm" disabled={userId === ''} isLoading={assign.isPending}>
        {t('institution.classes.assign')}
      </Button>
    </form>
  )
}
